import type { CivilAppellateWizardDraft } from '../../../types/civilAppellateCaseRequest';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import {
  contactNumberError,
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';
import type { CivilAppellateWizardStep } from './civilAppellateConstants';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import { appendInstituteValidationSpecs } from '../../../utils/instituteTypeValidation';
import { isHighUrgencyLevel } from './civilAppellateHelpers';

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function validateCivilAppellateStep(
  step: CivilAppellateWizardStep,
  draft: CivilAppellateWizardDraft,
  fileCategories: LookupItem[] = [],
  corporateInstitutes: LookupItem[] = [],
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'capl-fileCategory' },
      { key: 'subFileCategoryId', label: 'Sub File Category', value: draft.subFileCategoryId, elementId: 'capl-subFileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'capl-description' },
      { key: 'isAppealMatter', label: 'Appeal Matter', value: draft.isAppealMatter, elementId: 'capl-appealMatter' },
      { key: 'provinceId', label: 'Province', value: draft.provinceId, elementId: 'capl-province' },
      { key: 'districtId', label: 'District', value: draft.districtId, elementId: 'capl-district' },
      { key: 'yourReferenceNumber', label: 'Your Reference Number (External)', value: draft.yourReferenceNumber, elementId: 'capl-yourRef' },
      { key: 'hasConnectedMatters', label: 'Connected matters', value: draft.hasConnectedMatters, elementId: 'capl-connected' },
    );

    appendInstituteValidationSpecs(specs, draft, draft.fileCategoryId, fileCategories, 'capl', corporateInstitutes);

    if (draft.isAppealMatter === 'yes') {
      specs.push(
        { key: 'originalCourt', label: 'Original Court', value: draft.originalCourt, elementId: 'capl-originalCourt' },
      );
      if (!draft.judgmentOrOrderDocuments.some((f) => f.fileName.trim() || f.originalFileName.trim())) {
        extra.judgmentOrOrderDocuments = 'Upload at least one Judgement/Order document.';
      }
    }

    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: 'Connected Case Number', value: draft.connectedCaseNumber, elementId: 'capl-connectedCase' },
        
      );
      if (draft.connectedAgdFileRefAvailable) {
        specs.push(
          { key: 'connectedAgdFileRef', label: 'Connected AGD File Reference Number', value: draft.connectedAgdFileRef, elementId: 'capl-connectedAgd' },
        );
      }
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.originalCourt, MAX_REF)) extra.originalCourt = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
  }

  if (step >= 2) {
    specs.push(
      { key: 'plaintiffName', label: 'Plaintiff / Applicant Name', value: draft.plaintiffName, elementId: 'capl-plaintiff' },
      { key: 'defendantName', label: 'Defendant / Respondent Name', value: draft.defendantName, elementId: 'capl-defendant' },
      { key: 'legalOfficerName', label: 'Name of the Legal Officer/ Officer In-Charge', value: draft.legalOfficerName, elementId: 'capl-legalOfficer' },
      { key: 'legalOfficerContact', label: 'Contact Number', value: draft.legalOfficerContact, elementId: 'capl-legalContact' },
    );

    if (exceedsMax(draft.plaintiffName, MAX_REF)) extra.plaintiffName = maxCharLimitExceededError();
    if (exceedsMax(draft.defendantName, MAX_REF)) extra.defendantName = maxCharLimitExceededError();
    if (exceedsMax(draft.otherInvolvedInstitutions, MAX_DESC)) extra.otherInvolvedInstitutions = maxCharLimitExceededError();
    if (exceedsMax(draft.legalOfficerName, MAX_REF)) extra.legalOfficerName = maxCharLimitExceededError();

    const contactErr = contactNumberError(draft.legalOfficerContact);
    if (contactErr) extra.legalOfficerContact = contactErr;
  }

  if (step >= 3) {
    specs.push(
      { key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'capl-urgency' },
    );

    if (isHighUrgencyLevel(draft.urgencyLevelName)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'capl-urgencyJustification',
      });
    }

    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 4) {
    specs.push({ key: 'deliveryMethodIds', label: 'Delivery Method', value: draft.deliveryMethodIds, elementId: 'capl-delivery' });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'capl-deliveryOther',
      });
    }
    if (exceedsMax(draft.deliveryOther, MAX_REF)) extra.deliveryOther = maxCharLimitExceededError();
  }

  if (step >= 5) {
    Object.assign(extra, validateStep5Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep5Documents(draft: CivilAppellateWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}

export { isHighUrgencyLevel, minDeadlineDateForCivilAppellate } from './civilAppellateHelpers';
